import { CircleCheck, Loader2, RotateCw, TriangleAlert, Unplug } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchHealth, type HealthView } from '../lib/health';

type BadgeState = { view: 'loading' } | HealthView;

interface Presentation {
  label: string;
  Icon: typeof CircleCheck;
  spin: boolean;
  /** Semantic colour (never the ONLY status signal — the label + icon carry it too). */
  tone: string;
  dot: string;
}

const PRESENTATION: Record<BadgeState['view'], Presentation> = {
  loading: { label: 'Checking status', Icon: Loader2, spin: true, tone: 'text-muted', dot: 'bg-muted' },
  healthy: { label: 'Operational', Icon: CircleCheck, spin: false, tone: 'text-success', dot: 'bg-success' },
  degraded: { label: 'Degraded', Icon: TriangleAlert, spin: false, tone: 'text-warning', dot: 'bg-warning' },
  cannot_reach: { label: 'Unreachable', Icon: Unplug, spin: false, tone: 'text-error', dot: 'bg-error' },
};

/**
 * Live health badge. Fetches /api/health on mount and shows four states:
 * loading -> healthy / degraded / cannot_reach. Status is conveyed by an icon (shape) AND a
 * text label — never colour alone (WCAG 2.1 AA, interview Q17). The region is aria-live so
 * screen readers announce changes; the retry control is keyboard-operable.
 */
export function HealthBadge() {
  const [state, setState] = useState<BadgeState>({ view: 'loading' });
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setState({ view: 'loading' });
    try {
      setState(await fetchHealth());
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  const p = PRESENTATION[state.view];
  const message = state.view === 'loading' ? 'Contacting the server…' : state.message;

  return (
    <div
      className="mx-auto flex max-w-prose flex-col items-center gap-3 rounded-lg border border-border bg-surface px-6 py-5 shadow-sm"
      role="status"
      aria-live="polite"
    >
      <span className="flex items-center gap-2.5">
        <span aria-hidden="true" className={`inline-block h-2 w-2 rounded-full ${p.dot}`} />
        <p.Icon
          aria-hidden="true"
          className={`h-5 w-5 ${p.tone} ${p.spin ? 'motion-safe:animate-spin' : ''}`}
        />
        <span className={`font-sans text-sm font-semibold uppercase tracking-wider ${p.tone}`}>
          {p.label}
        </span>
      </span>
      <p className="text-center font-serif text-lg leading-snug text-ink">{message}</p>
      {state.view !== 'loading' && (
        <button
          type="button"
          onClick={() => void check()}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 font-sans text-sm text-ink transition-colors duration-150 ease-standard hover:bg-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <RotateCw aria-hidden="true" className="h-4 w-4" />
          Check again
        </button>
      )}
    </div>
  );
}

export default HealthBadge;
